import type { SnapPrivacyRedactionPolicy } from '../types';

export interface HiddenStyleRestore {
  el: HTMLElement;
  visibility: string;
  opacity: string;
}

const HEAVY_TAGS = new Set(['iframe', 'video', 'object', 'embed']);

const DEFAULT_SECRET_NAME = /password|passwd|secret|token|api[_-]?key|cvv|cvc|card|ssn|private/i;

export function isHeavyCaptureElement(el: Element): boolean {
  const tag = (el.tagName || '').toLowerCase();
  if (HEAVY_TAGS.has(tag)) return true;
  if (tag === 'canvas' && el.id !== 'nv-snap-canvas') return true;
  if (el.classList?.contains('apexcharts-canvas')) return true;
  return false;
}

export function isExcludedElement(
  el: Element,
  policy: SnapPrivacyRedactionPolicy
): boolean {
  if (!el || el.nodeType !== 1) return false;
  const html = el as HTMLElement;

  if (html.hasAttribute?.('data-no-capture')) return true;
  if (html.hasAttribute?.('data-no-screenshot')) return true;
  if (html.hasAttribute?.('data-nv-redact')) return true;
  if (html.closest?.('[data-no-capture],[data-no-screenshot],[data-nv-redact]')) {
    return true;
  }
  if (html.id === 'nv-snap-capturing-hud' || html.closest?.('#nv-snap-root')) {
    return true;
  }
  if (isHeavyCaptureElement(el)) return true;

  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input') {
    const type = (html.getAttribute('type') || 'text').toLowerCase();
    if (policy.neverCapturePasswords && type === 'password') return true;
    if (policy.maskInputTypes.map((t) => t.toLowerCase()).includes(type)) return true;
    if (policy.neverCaptureTokensOrSecrets) {
      const name = `${html.getAttribute('name') || ''} ${html.getAttribute('autocomplete') || ''}`.toLowerCase();
      if (DEFAULT_SECRET_NAME.test(name)) return true;
      if ((html.getAttribute('autocomplete') || '').toLowerCase() === 'cc-number') return true;
    }
  }
  if (tag === 'textarea' && policy.neverCaptureTokensOrSecrets) {
    const name = `${html.getAttribute('name') || ''} ${html.id || ''}`.toLowerCase();
    if (DEFAULT_SECRET_NAME.test(name)) return true;
  }
  return false;
}

function safeQueryAll(selector: string): Element[] {
  try {
    return Array.from(document.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/**
 * Hide never-capture / policy-selected elements before rasterization.
 * Algorithm adapted from Panel snap-screen.js (reusable); chrome selectors replaced.
 */
export function hideExcludedElements(
  policy: SnapPrivacyRedactionPolicy
): HiddenStyleRestore[] {
  const restored: HiddenStyleRestore[] = [];
  const seen = new Set<Element>();

  const baseSelectors = [
    '[data-no-capture]',
    '[data-no-screenshot]',
    '[data-nv-redact]',
    'input[type="password"]',
    'input[type="hidden"]',
    'input[autocomplete="cc-number"]',
    'iframe',
    'video',
    'object',
    'embed',
    'canvas:not(#nv-snap-canvas)',
    '#nv-snap-root',
    '#nv-snap-capturing-hud'
  ];

  const selectors = [
    ...baseSelectors,
    ...policy.maskAttributeSelectors,
    ...policy.maskCssSelectors
  ];

  for (const sel of selectors) {
    for (const el of safeQueryAll(sel)) {
      if (seen.has(el) || !(el instanceof HTMLElement)) continue;
      seen.add(el);
      restored.push({
        el,
        visibility: el.style.visibility,
        opacity: el.style.opacity
      });
      el.style.visibility = 'hidden';
      el.style.opacity = '0';
    }
  }

  for (const field of safeQueryAll('input, textarea')) {
    if (!(field instanceof HTMLElement)) continue;
    if (!isExcludedElement(field, policy)) continue;
    if (seen.has(field)) continue;
    seen.add(field);
    restored.push({
      el: field,
      visibility: field.style.visibility,
      opacity: field.style.opacity
    });
    field.style.visibility = 'hidden';
    field.style.opacity = '0';
  }

  return restored;
}

export function restoreExcludedElements(restored: HiddenStyleRestore[]): void {
  for (const item of restored) {
    item.el.style.visibility = item.visibility;
    item.el.style.opacity = item.opacity;
  }
}

/**
 * Honest limitation statement for documentation / UI.
 * html2canvas cannot guarantee pixel-level exclusion of all CSS-painted secrets.
 */
export const REDACTION_LIMITATIONS = [
  'Redaction hides matching DOM nodes before rasterization; it is not a pixel-level OCR guarantee.',
  'Cross-origin images may be omitted or taint the canvas depending on CORS.',
  'CSS background images and canvas-drawn secrets may not be fully excluded.',
  'Users should review the preview and apply cover/redact annotations before submit.',
  'Query strings are omitted by default unless capture policy explicitly permits them.',
  'Cookies, Authorization headers, and Web Storage values are never read by this SDK.'
];
